import { useState, useCallback } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import Iconify from '../../iconify';
import CustomTooltip from './CustomTooltip';
import { PresenceSegments } from '../types';

const calculateCompetitorsOnly = (totalVisibilityRate: number) => {
  return 100 - totalVisibilityRate;
};

export default function PresenceRateChart({
  data,
  round = 0,
  height = 6,
  totalVisibilityRate,
  colors: customColors,
}: {
  data: PresenceSegments;
  height?: number;
  totalVisibilityRate?: number;
  colors?: string[];
  round?: number;
}) {
  const defaultColors = [
    '#18A155',
    '#C09910',
    '#F9DE81',
    'rgba(145, 158, 171, 0.20)',
  ];

  const colors =
    customColors && customColors.length === 4
      ? customColors
      : defaultColors;

  const visibilityBase =
    typeof totalVisibilityRate === 'number' ? totalVisibilityRate : 100;

  const rawItems = [
    {
      label: 'Organic only',
      value: Math.max(0, data.organicOnly),
      color: colors[0],
      icon: 'solar:global-linear',
    },
    {
      label: 'Solo bidder',
      value: Math.max(0, data.soloBidder),
      color: colors[1],
      icon: 'carbon:user',
    },
    {
      label: 'Top ad position',
      value: Math.max(0, data.topAdPosition),
      color: colors[2],
      icon: 'carbon:user-multiple',
    },
    {
      label: 'Competitors only',
      value: Math.max(0, calculateCompetitorsOnly(visibilityBase)),
      color: colors[3],
      icon: 'ri:sword-line',
    },
  ];

  const items = rawItems.map((it) => ({
    ...it,
    value:
      round === 0 ? Math.round(it.value) : Number(it.value.toFixed(round)),
  }));

  // On ne garde que les segments visibles pour éviter les gaps fantômes
  const visibleItems = items.filter((it) => it.value > 0);

  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <CustomTooltip
      placement="top"
      disableInteractive
      noPadding
      open={open}
      onClose={handleClose}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 'none',
            width: '320px',
            boxSizing: 'border-box',
            p: 0,
          },
        },
      }}
      title={
        <Stack direction="column">
          <Stack
            sx={{
              pt: '12px',
              px: '16px',
              pb: 0,
              gap: '4px',
              alignItems: 'flex-start',
              alignSelf: 'stretch',
            }}
          >
            <Typography
              sx={{
                color: '#333C47',
                fontFamily: '"Public Sans"',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '100%',
              }}
            >
              Presence breakdown
            </Typography>
          </Stack>

          <Stack
            direction="column"
            sx={{
              pt: '12px',
              px: '16px',
              pb: '12px',
            }}
          >
            {items.map((it) => (
              <Stack
                key={it.label}
                direction="row"
                alignItems="center"
                sx={{
                  width: '100%',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Iconify
                    icon={it.icon}
                    sx={{
                      width: 14,
                      height: 14,
                      color: it.color,
                      flexShrink: 0,
                    }}
                  />

                  <Typography
                    sx={{
                      color: '#333C47',
                      fontFamily: '"Public Sans"',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '22px',
                    }}
                  >
                    {it.label}
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    color: '#637381',
                    fontVariantNumeric: 'lining-nums tabular-nums',
                    fontFamily: '"Public Sans"',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '22px',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {it.value}%
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      }
    >
      <Box
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        sx={{
          width: '100%',
          minWidth: 0,
        }}
      >
        <Box
          aria-label="presence-rate-bar"
          sx={{
            display: 'flex',
            gap: '2px',
            width: '100%',
            height: `${height}px`,
            borderRadius: '2px',
            overflow: 'hidden',
          }}
        >
          {visibleItems.map((it) => (
            <Box
              key={it.label}
              sx={{
                flex: it.value,
                height: '100%',
                backgroundColor: it.color,
                transition: 'all 0.2s ease',
                borderRadius: '2px',
                minWidth: 2,
              }}
            />
          ))}
        </Box>
      </Box>
    </CustomTooltip>
  );
}